export async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export type UploadProgress = {
  /** Poslani dio tijela zahtjeva, 0–1. */
  fraction: number;
  /** Tijelo je cijelo poslano; čeka se još samo odgovor servera. */
  sent: boolean;
};

/**
 * POST multipart forme s praćenjem napretka. `fetch` ne javlja koliko je tijela
 * poslano, pa upload ide preko XMLHttpRequesta. Greške su iste kao kod `fetchJson`.
 */
export function uploadJson<T>(
  url: string,
  body: FormData,
  opts: { onProgress?: (p: UploadProgress) => void; signal?: AbortSignal } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const { onProgress, signal } = opts;
    const aborted = () => new DOMException("Slanje prekinuto", "AbortError");
    if (signal?.aborted) return reject(aborted());

    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.responseType = "text";

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        onProgress?.({ fraction: e.loaded / e.total, sent: false });
      }
    };
    xhr.upload.onload = () => onProgress?.({ fraction: 1, sent: true });

    xhr.onload = () => {
      let parsed: any = null;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        // Odgovor nije JSON (npr. proxy greška) — ispod pada na statusText.
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        return reject(new Error(parsed?.error || xhr.statusText || "Greška pri slanju"));
      }
      resolve(parsed as T);
    };
    xhr.onerror = () => reject(new Error("Nema veze sa serverom"));
    xhr.onabort = () => reject(aborted());

    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}
